/**
 * {vendor}の詳細の節「{order}」（#233、docs/93 §4.6・§6、Figma ③-2）。{vendor}（外注先・両方）でモジュール sales の会社だけ。
 *   {orderAck}（会社に従う（会社：○）／オン／オフ＋オンのとき受け取り方 電子承諾｜書面＝#256。データは order_ack_mode の none／electronic／paper）・
 *   種別（工事／仕入。業種の既定は製品から。field の会社だけ＝#250）・
 *   電子でよいと確認済み（元請の記録＝LINE・メール・書面と写し）・初回の承諾（画面）＝承諾が電子になる{vendor}か、記録があるときだけ。
 *   選んだ瞬間に保存（版つき＝ぶつかったら最新の版でもう一度）
 *   基本契約書（#233 2c、docs/93 §14.2・★9）：工事の{vendor}だけ（field の会社・種別「仕入」には出さない）。行「基本契約書」→ 窓で 締結日・名称・PDF（任意）。
 *     記録があれば{order}に約款を付けず「基本契約書による」の1行。追加・修正・削除は版つき（PARTNER_STALE は最新の版でもう一度）・audit_logs
 */
import { ORDER_ACK_LABEL, ORDER_ACK_RECEIVE, ORDER_SETTINGS_MSG as M, orderAckTurnOn, type BasicContractSave, type PaperPresetList, type PaperRef, type PartnerOrderInfo, type PartnerOrderUpdate } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, apiUpload, messageOf } from "../../api.js";
import { useModule } from "../../core/slots.js";
import { useMe } from "../../auth.js";
import { DateInput } from "../../components/DateInput.jsx";
import { FileDrop } from "../../components/FileDrop.jsx";
import { GroupList, GroupNavRow, GroupSelectRow, GroupValueRow } from "../../components/GroupList.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { fmtDateTimeShort, ymdShort } from "../../lib/dates.js";
import { todayJst } from "../../lib/num.js";
import { usePdfTab } from "../../lib/pdfOpen.js";

const METHOD_LABEL: Record<"screen" | "line" | "mail" | "paper", string> = { screen: "画面", line: M.consentMethodLine, mail: M.consentMethodMail, paper: M.consentMethodPaper };

export function PartnerOrderSection({ partnerId, disabled }: { partnerId: string; disabled: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const pdfTab = usePdfTab();
  const key = ["partner-order", partnerId];
  const q = useQuery({ queryKey: key, queryFn: () => api<PartnerOrderInfo>("GET", `/partners/${partnerId}/order`) });
  const [dlg, setDlg] = useState<{ on: string; method: "line" | "mail" | "paper"; file: File | null; busy: boolean; error?: string | undefined } | null>(null);
  /** 基本契約書の窓（file＝新しく選んだ PDF・dropFile＝今の PDF を外す） */
  const [bcDlg, setBcDlg] = useState<{ on: string; name: string; file: File | null; dropFile: boolean; busy: boolean; error?: string | undefined } | null>(null);
  const field = useModule("field");
  const admin = useMe().data?.user.role === "admin";
  const presets = useQuery({ queryKey: ["paper-presets"], queryFn: () => api<PaperPresetList>("GET", "/paper/presets") });
  const d = q.data;
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!d) return <p className="hint">読み込んでいます…</p>;
  const put = async (patch: Omit<PartnerOrderUpdate, "version">) => {
    const send = (version: string) => api<PartnerOrderInfo>("PUT", `/partners/${partnerId}/order`, { ...patch, version });
    try {
      let r: PartnerOrderInfo;
      try {
        r = await send(d.updatedAt);
      } catch (e) {
        if (!(e instanceof ApiError && e.code === "PARTNER_STALE")) throw e;
        r = await send((await api<PartnerOrderInfo>("GET", `/partners/${partnerId}/order`)).updatedAt);
      }
      qc.setQueryData(key, r);
      void qc.invalidateQueries({ queryKey: ["partner", partnerId] }); // 取引先の版が進んだ
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const saveConsent = async () => {
    if (!dlg) return;
    setDlg({ ...dlg, busy: true, error: undefined });
    try {
      const f = dlg.file;
      const copy = f ? await apiUpload<{ assetId: string }>(`/partners/${partnerId}/consent-copy`, f, f.type === "application/pdf" || /\.pdf$/i.test(f.name) ? "application/pdf" : f.type) : null;
      const r = await api<PartnerOrderInfo>("POST", `/partners/${partnerId}/consents`, { on: dlg.on, method: dlg.method, copyAssetId: copy?.assetId ?? null });
      qc.setQueryData(key, r);
      setDlg(null);
    } catch (e) {
      setDlg((x) => (x ? { ...x, busy: false, error: messageOf(e) } : x));
    }
  };
  /** 版つきで送る（ぶつかったら最新の版でもう一度＝この窓の値で上書き） */
  const withVersion = async (send: (version: string) => Promise<PartnerOrderInfo>): Promise<PartnerOrderInfo> => {
    try {
      return await send(d.updatedAt);
    } catch (e) {
      if (!(e instanceof ApiError && e.code === "PARTNER_STALE")) throw e;
      return send((await api<PartnerOrderInfo>("GET", `/partners/${partnerId}/order`)).updatedAt);
    }
  };
  const bc = d.basicContract;
  const saveBc = async () => {
    if (!bcDlg) return;
    setBcDlg({ ...bcDlg, busy: true, error: undefined });
    try {
      const f = bcDlg.file;
      const up = f ? await apiUpload<{ assetId: string }>(`/partners/${partnerId}/basic-contract/file`, f, "application/pdf") : null;
      const assetId = up ? up.assetId : bcDlg.dropFile ? null : undefined;
      const body = (version: string): BasicContractSave => ({ on: bcDlg.on, name: bcDlg.name.trim(), ...(assetId !== undefined ? { assetId } : {}), version });
      const r = await withVersion((v) => api<PartnerOrderInfo>("PUT", `/partners/${partnerId}/basic-contract`, body(v)));
      qc.setQueryData(key, r);
      void qc.invalidateQueries({ queryKey: ["partner", partnerId] });
      setBcDlg(null);
    } catch (e) {
      setBcDlg((x) => (x ? { ...x, busy: false, error: messageOf(e) } : x));
    }
  };
  const deleteBc = async () => {
    if (!bcDlg) return;
    setBcDlg({ ...bcDlg, busy: true, error: undefined });
    try {
      const r = await withVersion((v) => api<PartnerOrderInfo>("DELETE", `/partners/${partnerId}/basic-contract?version=${encodeURIComponent(v)}`));
      qc.setQueryData(key, r);
      void qc.invalidateQueries({ queryKey: ["partner", partnerId] });
      setBcDlg(null);
    } catch (e) {
      setBcDlg((x) => (x ? { ...x, busy: false, error: messageOf(e) } : x));
    }
  };
  // 工事の{vendor}だけ（field の会社）。種別「仕入」には出さない
  const showBc = field && (d.workKind ?? d.workKindDefault) === "construction";
  // {orderAck}（#256）：会社に従う（""）／オン（電子承諾・書面）／オフ（none）
  const ackValue = d.ackMode == null ? "" : d.ackMode === "none" ? "off" : "on";
  const companyAckText = d.companyAck === "none" ? M.ackCompanyOff : ORDER_ACK_LABEL[d.companyAck];
  // 電子でよいと確認済み：承諾が電子になる{vendor}（{vendor}の指定か会社の既定）か、もう記録があるとき
  const showConsents = (d.ackMode ?? d.companyAck) === "electronic" || d.records.length > 0 || !!d.firstScreen;
  return (
    <div data-testid="partner-order">
      <GroupList>
        <GroupSelectRow
          id="pd_orderAck"
          label={t(M.ackMode)}
          value={ackValue}
          options={[
            { value: "", label: M.ackCompanyNow.replace("{v}", companyAckText) },
            { value: "on", label: M.ackOn },
            { value: "off", label: M.ackOff },
          ]}
          // オンにした瞬間の受け取り方は会社の受け取り方（会社がオフなら書面）＝docs/93 §17.3 ロ
          onChange={(v) => void put({ ackMode: v === "on" ? orderAckTurnOn(d.companyAck) : v === "off" ? "none" : null })}
          disabled={disabled}
          selectTestId="partner-order-ack"
        />
        {d.ackMode && d.ackMode !== "none" ? (
          <GroupValueRow
            className="is-sub"
            title={M.ackReceive}
            titleId="pd-ack-receive"
            value={
              <Segmented
                labelledBy="pd-ack-receive"
                value={d.ackMode}
                disabled={disabled}
                onChange={(v) => void put({ ackMode: v })}
                options={ORDER_ACK_RECEIVE.map((v) => ({ value: v, label: ORDER_ACK_LABEL[v], testId: `partner-order-ack-receive-${v}` }))}
              />
            }
            testId="partner-order-ack-receive"
          />
        ) : null}
        {/* 種別（工事／仕入）は建設の紙を使う field の会社だけ（#250。営業力はいつも仕入の紙） */}
        {field ? (
          <GroupSelectRow
            id="pd_orderKind"
            label={M.workKind}
            value={d.workKind ?? ""}
            options={[
              { value: "", label: `${d.workKindDefault === "construction" ? "工事" : "仕入"}（既定）` },
              { value: "construction", label: "工事" },
              { value: "supply", label: "仕入" },
            ]}
            onChange={(v) => void put({ workKind: v === "construction" || v === "supply" ? v : null })}
            disabled={disabled}
            selectTestId="partner-order-kind"
          />
        ) : null}
        {/* 書式（#233 2d）：この{vendor}に出す{order}の書式（元請・{vendor}指定の様式など）。管理者だけ。null＝会社の既定 */}
        <GroupSelectRow
          id="pd_orderPaper"
          label="書式"
          value={d.paper ?? ""}
          options={[
            { value: "", label: "会社の既定" },
            ...(presets.data?.presets.filter((p) => p.kinds.includes("order")).map((p) => ({ value: `preset:${p.key}`, label: t(p.name) })) ?? []),
            ...(presets.data?.own.order.map((x) => ({ value: `template:${x.id}`, label: `${x.name}（カスタム書式）` })) ?? []),
          ]}
          onChange={(v) => void put({ paper: v ? (v as PaperRef) : null })}
          disabled={disabled || !admin}
          selectTestId="partner-order-paper"
        />
        {showBc ? (
          <GroupNavRow
            title={M.basicContract}
            value={bc ? `${ymdShort(bc.on)} ${bc.name}` : M.basicContractNone}
            onClick={() => setBcDlg({ on: bc?.on ?? todayJst(), name: bc?.name ?? "", file: null, dropFile: false, busy: false })}
            disabled={disabled}
            buttonTestId="partner-basic-contract"
            valueTestId="partner-basic-contract-value"
          />
        ) : null}
      </GroupList>
      {showConsents ? (
        <div className="oconsent" data-testid="partner-consents">
          <div className="row" style={{ alignItems: "center", gap: 6, marginTop: 12 }}>
            <b>{M.consents}</b>
            <Help text={t(M.consentsHelp)} testId="help-partner-consents" />
            <span className="sp" />
            <span className={d.consented ? "chip ai" : "chip quiet"} data-testid="partner-consented">{d.consented ? "確認済み" : "未確認"}</span>
            {disabled ? null : (
              <button type="button" className="btn tonal sm" onClick={() => setDlg({ on: todayJst(), method: "line", file: null, busy: false })} data-testid="partner-consent-add">
                {M.consentAdd}
              </button>
            )}
          </div>
          {d.records.length || d.firstScreen ? (
            <ul className="olist">
              {d.firstScreen ? (
                <li data-testid="partner-consent-first">
                  <span className="muted">{M.firstScreen}</span>　{M.firstScreenText.replace("{at}", fmtDateTimeShort(d.firstScreen.at)).replace("{no}", d.firstScreen.docNo ?? "—")}
                </li>
              ) : null}
              {d.records.map((r) => (
                <li key={r.id} data-testid="partner-consent-row">
                  <span className="num">{ymdShort(r.on)}</span>　{r.event === "revoke" ? "撤回" : METHOD_LABEL[r.method]}
                  {r.copyAssetId ? (
                    <button type="button" className="btn text sm" disabled={!!pdfTab.busy} onClick={() => void pdfTab.open(`/api/v1/partners/${partnerId}/consents/${r.id}/copy`)}>
                      {M.consentShow}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {bcDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !bcDlg.busy && setBcDlg(null)}>
          <div className="dlg" role="dialog" aria-label={M.basicContract} onKeyDown={(e) => e.key === "Escape" && !bcDlg.busy && setBcDlg(null)} data-testid="partner-bc-dialog">
            <div className="dh row" style={{ alignItems: "center", gap: 6 }}>
              {M.basicContract}
              <Help label={`${M.basicContract}の説明`} text={t(M.basicContractHelp)} testId="help-partner-bc" />
            </div>
            <div className="db">
              <label className="olbl" htmlFor="pbc_on">{M.basicContractOn}</label>
              <DateInput id="pbc_on" label={M.basicContractOn} value={bcDlg.on} clearable={false} onChange={(v) => v && setBcDlg({ ...bcDlg, on: v })} testId="partner-bc-on" />
              <label className="olbl" htmlFor="pbc_name" style={{ marginTop: 12 }}>{M.basicContractName}</label>
              <input id="pbc_name" className="inp" maxLength={60} value={bcDlg.name} onChange={(e) => setBcDlg({ ...bcDlg, name: e.target.value })} data-testid="partner-bc-name" />
              <span className="olbl" style={{ marginTop: 12, display: "block" }}>{M.basicContractFile}</span>
              {bcDlg.file ? (
                <p className="row" style={{ gap: 8, margin: "4px 0", alignItems: "center", flexWrap: "wrap" }}>
                  <span data-testid="partner-bc-file-name">{bcDlg.file.name}</span>
                  <button type="button" className="btn text sm" disabled={bcDlg.busy} onClick={() => setBcDlg({ ...bcDlg, file: null })}>{M.basicContractFileRemove}</button>
                </p>
              ) : bc?.fileUrl && !bcDlg.dropFile ? (
                <p className="row" style={{ gap: 8, margin: "4px 0", alignItems: "center", flexWrap: "wrap" }}>
                  <a className="btn text sm" href={bc.fileUrl} target="_blank" rel="noreferrer" data-testid="partner-bc-file-show">{M.basicContractFileShow}</a>
                  <button type="button" className="btn text sm" disabled={bcDlg.busy} onClick={() => setBcDlg({ ...bcDlg, dropFile: true })} data-testid="partner-bc-file-remove">{M.basicContractFileRemove}</button>
                </p>
              ) : (
                <FileDrop
                  accept="application/pdf,.pdf"
                  phoneAccept="application/pdf,.pdf"
                  multiple={false}
                  compact
                  hint="ここに PDF をドロップ"
                  disabled={bcDlg.busy}
                  onFiles={(fs) => {
                    const f = fs[0];
                    if (!f) return;
                    if (!(f.type === "application/pdf" || /\.pdf$/i.test(f.name))) return setBcDlg({ ...bcDlg, error: M.basicContractPdfOnly });
                    setBcDlg({ ...bcDlg, file: f, error: undefined });
                  }}
                  testId="partner-bc-drop"
                  inputTestId="partner-bc-file"
                />
              )}
              <ErrorText>{bcDlg.error}</ErrorText>
            </div>
            <div className="df">
              {bc ? (
                <button type="button" className="btn shu" disabled={bcDlg.busy} onClick={() => void deleteBc()} data-testid="partner-bc-delete">削除</button>
              ) : null}
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={bcDlg.busy} onClick={() => setBcDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={bcDlg.busy || !bcDlg.on || !bcDlg.name.trim()} onClick={() => void saveBc()} data-testid="partner-bc-save">決定</button>
            </div>
          </div>
        </div>
      ) : null}
      {dlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !dlg.busy && setDlg(null)}>
          <div className="dlg" role="dialog" aria-label={M.consentAddTitle} onKeyDown={(e) => e.key === "Escape" && setDlg(null)} data-testid="partner-consent-dialog">
            <div className="dh">{M.consentAddTitle}</div>
            <div className="db">
              <label className="olbl" htmlFor="pc_on">{M.consentOn}</label>
              <DateInput id="pc_on" label={M.consentOn} value={dlg.on} clearable={false} onChange={(v) => v && setDlg({ ...dlg, on: v })} testId="partner-consent-on" />
              <label className="olbl" htmlFor="pc_method" style={{ marginTop: 12 }}>{M.consentMethod}</label>
              <select id="pc_method" className="inp" value={dlg.method} onChange={(e) => setDlg({ ...dlg, method: e.target.value as "line" | "mail" | "paper" })} data-testid="partner-consent-method">
                <option value="line">{M.consentMethodLine}</option>
                <option value="mail">{M.consentMethodMail}</option>
                <option value="paper">{M.consentMethodPaper}</option>
              </select>
              <label className="olbl" htmlFor="pc_copy" style={{ marginTop: 12 }}>{M.consentCopy}</label>
              <input id="pc_copy" type="file" accept="application/pdf,.pdf,image/png,image/jpeg" onChange={(e) => setDlg({ ...dlg, file: e.target.files?.[0] ?? null })} data-testid="partner-consent-file" />
              <ErrorText>{dlg.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={dlg.busy} onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={dlg.busy || !dlg.on} onClick={() => void saveConsent()} data-testid="partner-consent-save">{M.consentSave}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
